import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, Users, Check, FileText, BookOpen } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export default function AcceptSprintInvitation() {
  const { invitationId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [invitation, setInvitation] = useState<any>(null);
  const [sprint, setSprint] = useState<any>(null);
  const [inviter, setInviter] = useState<any>(null);
  const [participantCount, setParticipantCount] = useState(0);
  const [contentCount, setContentCount] = useState({ files: 0, kbs: 0 });
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadInvitationDetails();
  }, [invitationId]);

  const loadInvitationDetails = async () => {
    try {
      setLoading(true);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);

      // Load invitation
      const { data: inviteData, error: inviteError } = await supabase
        .from("sprint_invitations")
        .select("*")
        .eq("id", invitationId)
        .single();

      if (inviteError) throw new Error("Invitation not found");

      setInvitation(inviteData);

      // If already accepted, redirect to sprint
      if (inviteData.status === "accepted") {
        toast({
          title: "Already accepted",
          description: "This invitation has already been accepted",
        });
        navigate(`/prompt-architecture-sprint?sprint=${inviteData.sprint_id}`);
        return;
      }

      // Load sprint details
      const { data: sprintData, error: sprintError } = await supabase
        .from("prompt_architecture_sprints")
        .select("*")
        .eq("id", inviteData.sprint_id)
        .single();

      if (sprintError) throw new Error("Sprint not found");

      setSprint(sprintData);

      // Load inviter profile
      const { data: inviterData, error: inviterError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", inviteData.inviter_id)
        .single();

      if (inviterError) throw new Error("Inviter profile not found");

      setInviter(inviterData);

      // Load participant count
      const { count: pCount } = await supabase
        .from("sprint_participants")
        .select("*", { count: "exact", head: true })
        .eq("sprint_id", inviteData.sprint_id);

      setParticipantCount(pCount || 0);

      // Load content count
      const { count: fileCount } = await supabase
        .from("sprint_inform_content")
        .select("*", { count: "exact", head: true })
        .eq("sprint_id", inviteData.sprint_id)
        .eq("content_type", "file");

      const { count: kbCount } = await supabase
        .from("sprint_inform_content")
        .select("*", { count: "exact", head: true })
        .eq("sprint_id", inviteData.sprint_id)
        .eq("content_type", "knowledge_base");

      setContentCount({ files: fileCount || 0, kbs: kbCount || 0 });

      // Validate email match if user is logged in
      if (user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("email")
          .eq("id", user.id)
          .single();

        if (profile && profile.email.toLowerCase() !== inviteData.invitee_email.toLowerCase()) {
          setError(`This invitation was sent to ${inviteData.invitee_email}. Please log in with that email address.`);
        }
      }

    } catch (err: any) {
      console.error("Error loading invitation:", err);
      setError(err.message || "Failed to load invitation details");
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!currentUser) {
      // Store invitation ID and redirect to auth
      sessionStorage.setItem("pendingSprintInvitation", invitationId || "");
      navigate("/auth");
      return;
    }

    // Check if user has a username (profile complete)
    const { data: profileData } = await supabase
      .from('profiles')
      .select('username')
      .eq('id', currentUser.id)
      .single();

    if (!profileData?.username) {
      // Store invitation and redirect to profile creation
      sessionStorage.setItem('pendingSprintInvitation', invitationId || "");
      navigate('/create-myspai-profile');
      toast({
        title: "Profile setup required",
        description: "Please complete your mySPAI profile before joining the sprint"
      });
      return;
    }

    setAccepting(true);

    try {
      // Update invitation status
      const { error: updateError } = await supabase
        .from("sprint_invitations")
        .update({
          status: "accepted",
          accepted_at: new Date().toISOString(),
          invitee_user_id: currentUser.id,
        })
        .eq("id", invitationId);

      if (updateError) throw updateError;

      // Add user to sprint participants
      const { error: participantError } = await supabase
        .from("sprint_participants")
        .insert({
          sprint_id: invitation.sprint_id,
          user_id: currentUser.id,
          role: "contributor",
        });

      if (participantError && !participantError.message.includes("duplicate")) {
        throw participantError;
      }

      toast({
        title: "Welcome to the sprint!",
        description: "You've successfully joined the sprint",
      });

      navigate(`/prompt-architecture-sprint?sprint=${invitation.sprint_id}`);
    } catch (err: any) {
      console.error("Error accepting invitation:", err);
      toast({
        title: "Failed to accept invitation",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = () => {
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md p-8 text-center">
          <div className="text-destructive mb-4">
            <Users className="w-16 h-16 mx-auto opacity-50" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Invitation Not Found</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => navigate("/")}>Go Home</Button>
        </Card>
      </div>
    );
  }

  const currentStage = sprint?.inform_stage_completed
    ? sprint?.decide_stage_completed
      ? sprint?.execute_stage_completed
        ? sprint?.assess_stage_completed
          ? "Completed"
          : "Stage 4: Assess"
        : "Stage 3: Execute"
      : "Stage 2: Decide"
    : "Stage 1: Inform";

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-accent/10 py-12 px-4">
      <Card className="max-w-2xl mx-auto p-8">
        <div className="text-center space-y-4 mb-8">
          <div className="flex items-center justify-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
              <Users className="w-10 h-10 text-primary" />
            </div>
          </div>
          <h1 className="text-3xl font-bold">You're Invited to a Sprint!</h1>
          {inviter && (
            <div className="flex items-center justify-center gap-3">
              <Avatar className="w-12 h-12">
                <AvatarImage src={inviter.avatar_url} />
                <AvatarFallback>
                  {inviter.display_name?.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="text-left">
                <p className="font-medium">{inviter.display_name}</p>
                <p className="text-sm text-muted-foreground">has invited you to collaborate</p>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="p-4 bg-accent/10 rounded-lg border border-accent/20">
            <p className="text-sm text-muted-foreground mb-1">Sprint Objective</p>
            <p className="font-semibold text-lg">{sprint?.objective}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-background border rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Progress</p>
              <p className="text-lg font-semibold">{currentStage}</p>
            </div>
            <div className="p-4 bg-background border rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Participants</p>
              <p className="text-lg font-semibold">{participantCount}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-background border rounded-lg flex items-center gap-3">
              <FileText className="w-8 h-8 text-primary/70" />
              <div>
                <p className="text-sm text-muted-foreground">Files</p>
                <p className="text-lg font-semibold">{contentCount.files}</p>
              </div>
            </div>
            <div className="p-4 bg-background border rounded-lg flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-primary/70" />
              <div>
                <p className="text-sm text-muted-foreground">Knowledge Bases</p>
                <p className="text-lg font-semibold">{contentCount.kbs}</p>
              </div>
            </div>
          </div>

          {invitation?.invitation_message && (
            <div className="p-4 border-l-4 border-primary bg-primary/5 rounded">
              <p className="text-sm text-muted-foreground mb-1">Personal message:</p>
              <p className="italic">"{invitation.invitation_message}"</p>
            </div>
          )}

          <div className="p-4 bg-accent/5 rounded-lg">
            <p className="font-medium mb-2">What's an IDEAS Sprint?</p>
            <p className="text-sm text-muted-foreground">
              A collaborative process to build validated knowledge with AI under smart contract governance.
              You'll be credited, scored, and compensated for your contributions through five stages:
              Inform, Decide, Execute, Assess, and Share.
            </p>
          </div>

          {error && (
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-destructive text-sm">{error}</p>
            </div>
          )}
        </div>

        <div className="mt-8 space-y-3">
          <Button
            size="lg"
            className="w-full"
            onClick={handleAcceptInvitation}
            disabled={accepting || !!error}
          >
            {accepting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Joining Sprint...
              </>
            ) : (
              <>
                <Check className="mr-2 h-4 w-4" />
                {currentUser ? "Accept Invitation & Join Sprint" : "Sign In to Accept"}
              </>
            )}
          </Button>
          <Button variant="outline" className="w-full" onClick={handleDecline}>
            Decline
          </Button>
        </div>
      </Card>
    </div>
  );
}
